import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { googleAuthRequest } from "../../../SocialAuth/socialAuthActions";
import { extractInvitationFromURL } from "../../../../utils/socialAuthHelpers";

/**
 * Custom hook for Google OAuth authentication with PKCE
 * Uses @react-oauth/google's Authorization Code flow
 *
 * @returns {Object} - { handleGoogleAuth, isLoading, error }
 */
const useGoogleAuth = (options = {}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const socialAuth = useSelector((state) => state.socialAuth);

  const { isLoading, errorMessage } = socialAuth;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getAttributionData = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const attributionParams = {};

    if (urlParams.get("gclid"))
      attributionParams.gclid = urlParams.get("gclid");
    if (urlParams.get("fbclid"))
      attributionParams.fbclid = urlParams.get("fbclid");
    if (urlParams.get("utm_source"))
      attributionParams.utm_source = urlParams.get("utm_source");
    if (urlParams.get("utm_campaign"))
      attributionParams.utm_campaign = urlParams.get("utm_campaign");

    return Object.keys(attributionParams).length > 0 ? attributionParams : null;
  }, [location.search]);

  /**
   * Handle successful Google OAuth
   * The response will contain just the authorization code
   * Backend will exchange it for tokens using redirect_uri='postmessage'
   */
  const onGoogleSuccess = useCallback(
    (response) => {
      const { invitationKey } = extractInvitationFromURL(location);

      if (!response.code) {
        return;
      }

      const payload = {
        code: response.code,
        ...(timezone && { timezone }),
        ...(invitationKey && { key: invitationKey }),
      };

      // Include attribution data if present (same as email signup)
      const attributionData = getAttributionData();
      if (attributionData) {
        payload.attribution_data = attributionData;
      }
      dispatch(googleAuthRequest(payload));
    },
    [dispatch, location]
  );

  /**
   * Handle Google OAuth error
   */
  const onGoogleError = useCallback((error) => {
    console.error("=== Google OAuth Error ===");
    console.error("Error:", error);
  }, []);


  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError: onGoogleError,
    flow: "auth-code",
    ...(options.loginHint ? { login_hint: options.loginHint } : {}),
  });

  /**
   * Trigger Google OAuth flow
   */
  const handleGoogleAuth = useCallback(() => {
    googleLogin();
  }, [googleLogin, options.loginHint]);

  return {
    handleGoogleAuth,
    isLoading,
    error: errorMessage,
  };
};

export default useGoogleAuth;
